import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useGetSearchCategoriesQuery } from '@Features/Search/SearchApi/SearchApi'

type Tint = 'blue' | 'yellow'
const SUBJECTS: { name: string; emoji: string; tint: Tint }[] = [
	{ name: 'كيمياء', emoji: '🧪', tint: 'blue' },
	{ name: 'فيزياء', emoji: '🧲', tint: 'blue' },
	{ name: 'أحياء', emoji: '🧬', tint: 'blue' },
	{ name: 'لغة عربية', emoji: '📖', tint: 'blue' },
	{ name: 'علم نفس واجتماع', emoji: '🧠', tint: 'blue' },
	{ name: 'فلسفة ومنطق', emoji: '💡', tint: 'blue' },
	{ name: 'تاريخ', emoji: '🏛️', tint: 'blue' },
	{ name: 'جغرافيا', emoji: '🗺️', tint: 'blue' },
	{ name: 'جيولوجيا', emoji: '🪨', tint: 'blue' },
	{ name: 'رياضيات', emoji: '🔢', tint: 'yellow' },
	{ name: 'English', emoji: '🇬🇧', tint: 'yellow' },
	{ name: 'French', emoji: '🇫🇷', tint: 'yellow' },
	{ name: 'German', emoji: '🇩🇪', tint: 'yellow' },
	{ name: 'Italian', emoji: '🇮🇹', tint: 'yellow' },
	{ name: 'Chemistry', emoji: '🧪', tint: 'blue' },
	{ name: 'Physics', emoji: '⚛️', tint: 'blue' },
]

type CategoriesProps = {
	/**
	 * Design chip grid (both landings): the Admin Portal's subjects as
	 * inline-icon chips, 3-up phones / 6-up desktop, start-aligned bold
	 * heading. Default keeps the legacy logged-in landing exactly as it was.
	 */
	compact?: boolean
	/** Overrides the compact variant's heading (e.g. the home's "تصفح بالمادة"). */
	heading?: React.ReactNode
}

const Categories: React.FC<CategoriesProps> = ({ compact, heading }) => {
	const [t] = useTranslation('landingPage', { keyPrefix: 'categories' })
	const navigate = useNavigate()
	// The same /categories the search المادة pill offers — the section shows
	// exactly what search can filter by, straight from the Admin Portal.
	const { data: categoriesRes } = useGetSearchCategoriesQuery(undefined, { skip: !compact })
	const categories = categoriesRes?.data ?? []

	if (compact) {
		// Loading, error, or an empty admin list: no half-rendered section.
		if (categories.length === 0) return null
		return (
			<DesignSection>
				<DesignHeading>
					{heading ?? (
						<>
							{t('sentence', { defaultValue: 'نقدم مجموعة متنوعة من' })} {t('subjects', { defaultValue: 'المواد الدراسية' })}
						</>
					)}
				</DesignHeading>
				<DesignGrid>
					{categories.map(c => (
						<DesignChip key={c.id} type="button" onClick={() => navigate(`/search?category=${c.id}`)}>
							{c.icon ? (
								<img className="e" src={c.icon} alt="" />
							) : (
								<span className="e" aria-hidden>
									📚
								</span>
							)}
							<span className="l">{c.name}</span>
						</DesignChip>
					))}
				</DesignGrid>
			</DesignSection>
		)
	}

	return (
		<Section>
			<Heading>
				{t('sentence', { defaultValue: 'نقدم مجموعة متنوعة من' })} <span>{t('subjects', { defaultValue: 'المواد الدراسية' })}</span>
			</Heading>
			<Grid>
				{SUBJECTS.map(s => (
					<Chip key={s.name} onClick={() => navigate(`/search?tab=classrooms&query=${encodeURIComponent(s.name)}`)}>
						<IconTile tint={s.tint}>{s.emoji}</IconTile>
						<Name>{s.name}</Name>
					</Chip>
				))}
			</Grid>
		</Section>
	)
}

export default Categories

/* ── Guest design replica ── */
const DesignSection = styled.section`
	padding: 12px 16px;
	display: flex;
	flex-direction: column;
	gap: 12px;
	text-align: start;
`
const DesignHeading = styled.h2`
	margin: 0;
	font: var(--fw-bold) var(--fs-body-lg) var(--font-sans);
	color: var(--text-primary);
	@media (min-width: 1024px) {
		font-size: 22px;
	}
`
const DesignGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 8px;
	@media (min-width: 1024px) {
		grid-template-columns: repeat(6, minmax(0, 1fr));
	}
`
const DesignChip = styled.button`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 10px 8px;
	background: var(--surface-card);
	border: 1px solid var(--border);
	border-radius: var(--r-lg);
	cursor: pointer;
	font-family: var(--font-sans);
	min-width: 0;
	.e {
		font-size: 16px;
		line-height: 1;
		flex: none;
	}
	img.e {
		width: 18px;
		height: 18px;
		object-fit: contain;
	}
	.l {
		font: var(--fw-medium) var(--fs-label) var(--font-sans);
		color: var(--text-primary);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
`

const Section = styled.section`
	max-width: 1180px;
	margin: 48px auto 0;
	padding: 0 16px;
	@media (min-width: 768px) {
		margin-top: 64px;
		padding: 0 24px;
	}
`
const Heading = styled.h2`
	margin: 0 0 24px;
	text-align: center;
	font: var(--fw-regular) 22px var(--font-sans);
	color: var(--text-primary);
	span {
		font-weight: var(--fw-bold);
		color: var(--blue);
	}
	@media (min-width: 768px) {
		font-size: 24px;
	}
`
const Grid = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 12px;
	@media (min-width: 600px) {
		grid-template-columns: repeat(4, minmax(0, 1fr));
	}
	@media (min-width: 1024px) {
		grid-template-columns: repeat(6, minmax(0, 1fr));
		gap: 14px;
	}
`
const Chip = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 12px 14px;
	background: var(--surface-card);
	border: 1px solid var(--border);
	border-radius: var(--r-lg);
	cursor: pointer;
	transition: border-color var(--dur-fast) var(--ease), box-shadow var(--dur-fast) var(--ease);
	&:hover {
		border-color: var(--blue-20);
		box-shadow: var(--sh-rest);
	}
`
const IconTile = styled.span<{ tint: Tint }>`
	width: 36px;
	height: 36px;
	flex: none;
	border-radius: var(--r-md);
	background: ${p => (p.tint === 'yellow' ? 'var(--yellow-12)' : 'var(--blue-12)')};
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 18px;
`
const Name = styled.span`
	font: var(--fw-bold) 14px var(--font-sans);
	color: var(--text-primary);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`
